/**
 * Godot-like script lifecycle for scene nodes.
 *
 * Lifecycle: _enter_tree → (children ready) → _ready → _process / _physics_process → _exit_tree
 * Also: signals, groups, Input, Engine.get_singleton, call_deferred, timers via get_tree().
 */

import type { SceneNodeData } from './types';
import type { SceneTree, TreeAPI } from './SceneTree';
import { SignalBus, type SignalCallable } from './Signal';
import { Input } from './InputMap';

export interface ScriptHost {
  log: (msg: string) => void;
  onScriptPropChange?: (nodeId: string, key: string, value: unknown) => void;
}

export interface NodeScriptAPI {
  id: string;
  name: string;
  type: string;
  path: string;
  visible: boolean;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  props: Record<string, unknown>;
  /** Script @export values (mutable) */
  [key: string]: unknown;

  get_parent(): NodeScriptAPI | null;
  get_children(): NodeScriptAPI[];
  get_node(path: string): NodeScriptAPI | null;
  get_node_or_null(path: string): NodeScriptAPI | null;
  find_child(name: string, recursive?: boolean): NodeScriptAPI | null;
  is_inside_tree(): boolean;
  get_tree(): TreeAPI;
  get_path(): string;

  set_position(x: number, y: number, z: number): void;
  set_rotation_degrees(x: number, y: number, z: number): void;
  rotate_y(rad: number): void;
  rotate_x(rad: number): void;
  rotate_z(rad: number): void;
  translate(x: number, y: number, z: number): void;
  look_at(x: number, y: number, z: number): void;
  set_visible(v: boolean): void;
  queue_free(): void;

  // Groups
  add_to_group(group: string): void;
  remove_from_group(group: string): void;
  is_in_group(group: string): boolean;
  get_groups(): string[];

  // Signals
  connect(signal: string, callable: SignalCallable): void;
  disconnect(signal: string, callable: SignalCallable): void;
  is_connected(signal: string, callable: SignalCallable): boolean;
  emit_signal(signal: string, ...args: unknown[]): void;
  has_signal(signal: string): boolean;

  // Deferred
  call_deferred(method: string, ...args: unknown[]): void;

  // Character helpers
  move_and_slide(vx: number, vy: number, vz: number, delta: number): void;

  log(...args: unknown[]): void;
  print(...args: unknown[]): void;
}

export type ScriptHandlers = {
  _enter_tree?: (this: NodeScriptAPI) => void;
  _ready?: (this: NodeScriptAPI) => void;
  _process?: (this: NodeScriptAPI, delta: number) => void;
  _physics_process?: (this: NodeScriptAPI, delta: number) => void;
  _exit_tree?: (this: NodeScriptAPI) => void;
  _notification?: (this: NodeScriptAPI, what: number) => void;
  [key: string]: unknown;
};

export interface LiveScriptInstance {
  nodeId: string;
  handlers: ScriptHandlers;
  api: NodeScriptAPI;
  ready: boolean;
  entered: boolean;
  error: string | null;
  signals: SignalBus;
}

const EXPORT_RE = /^\s*(?:\/\/\s*)?@export\s+(\w+)\s+(.+?)\s*$/gm;
const EXTENDS_RE = /^\s*(?:\/\/\s*)?@extends\s+(\w+)\s*$/m;
const SIGNAL_RE = /^\s*(?:\/\/\s*)?@signal\s+(\w+)\s*$/gm;

export function parseExports(source: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let m: RegExpExecArray | null;
  const re = new RegExp(EXPORT_RE);
  while ((m = re.exec(source))) {
    out[m[1]] = coerceLiteral(m[2].trim());
  }
  return out;
}

export function parseExtends(source: string): string | null {
  const m = source.match(EXTENDS_RE);
  return m ? m[1] : null;
}

export function parseSignals(source: string): string[] {
  const names: string[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(SIGNAL_RE);
  while ((m = re.exec(source))) names.push(m[1]);
  return names;
}

function coerceLiteral(raw: string): unknown {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null' || raw === 'undefined') return null;
  if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
    return raw.slice(1, -1);
  }
  const n = Number(raw);
  if (!Number.isNaN(n) && raw !== '') return n;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/** Globals injected into every script compile. */
export interface ScriptGlobals {
  Input: {
    is_action_pressed(action: string): boolean;
    is_action_just_pressed(action: string): boolean;
    is_action_just_released(action: string): boolean;
    get_axis(neg: string, pos: string): number;
    get_vector(nx: string, px: string, ny: string, py: string): { x: number; y: number };
    is_key_pressed(code: string): boolean;
  };
  Engine: {
    get_singleton(name: string): unknown;
    get_main_loop(): TreeAPI;
  };
  print: (...args: unknown[]) => void;
}

/** Compile user source into a handlers object. */
export function compileScript(source: string, host: ScriptHost, globals?: ScriptGlobals): ScriptHandlers {
  const cleaned = source.replace(/^\s*import\s+.+;?\s*$/gm, '');
  const factory = new Function(
    'host',
    'Input',
    'Engine',
    'print',
    `
    "use strict";
    const module = { exports: {} };
    const exports = module.exports;
    const console = {
      log: (...a) => host.log(a.map(String).join(' ')),
      warn: (...a) => host.log('[warn] ' + a.map(String).join(' ')),
      error: (...a) => host.log('[error] ' + a.map(String).join(' ')),
    };
    ${cleaned}
    if (module.exports && (typeof module.exports === 'function' || Object.keys(module.exports).length)) {
      const ex = module.exports.default !== undefined ? module.exports.default : module.exports;
      if (typeof ex === 'function') {
        try { return new ex(); } catch { return ex(); }
      }
      return ex;
    }
    const handlers = {};
    if (typeof _enter_tree === 'function') handlers._enter_tree = _enter_tree;
    if (typeof _ready === 'function') handlers._ready = _ready;
    if (typeof _process === 'function') handlers._process = _process;
    if (typeof _physics_process === 'function') handlers._physics_process = _physics_process;
    if (typeof _exit_tree === 'function') handlers._exit_tree = _exit_tree;
    if (typeof _notification === 'function') handlers._notification = _notification;
    return handlers;
  `,
  ) as (
    host: ScriptHost,
    Input: ScriptGlobals['Input'],
    Engine: ScriptGlobals['Engine'],
    print: (...a: unknown[]) => void,
  ) => ScriptHandlers;

  const g: ScriptGlobals = globals || {
    Input,
    Engine: {
      get_singleton: () => null,
      get_main_loop: () =>
        ({
          paused: false,
          current_scene: '',
          root: '/root',
          physics_ticks_per_second: 60,
          create_timer: async () => {},
          create_timer_callback: () => '',
          call_deferred: (fn: () => void) => fn(),
          call_group: () => {},
          get_nodes_in_group: () => [],
          change_scene_to_file: async () => false,
          quit: () => {},
          set_pause: () => {},
          get_frame: () => 0,
        }) as TreeAPI,
    },
    print: (...a: unknown[]) => host.log(a.map(String).join(' ')),
  };

  try {
    const result = factory(host, g.Input, g.Engine, g.print) || {};
    const exported = parseExports(source);
    for (const [k, v] of Object.entries(exported)) {
      if (result[k] === undefined) result[k] = v;
    }
    return result;
  } catch (e) {
    host.log(`Script compile error: ${(e as Error).message}`);
    throw e;
  }
}

export interface TreeNodeLink {
  data: SceneNodeData;
  parent: TreeNodeLink | null;
  children: TreeNodeLink[];
  object3d: import('three').Object3D | null;
  script: LiveScriptInstance | null;
  signals: SignalBus;
}

export function buildPath(link: TreeNodeLink): string {
  const parts: string[] = [];
  let cur: TreeNodeLink | null = link;
  while (cur) {
    parts.unshift(cur.data.name);
    cur = cur.parent;
  }
  return '/' + parts.join('/');
}

export function createNodeAPI(
  link: TreeNodeLink,
  all: Map<string, TreeNodeLink>,
  host: ScriptHost,
  tree: SceneTree,
  onQueueFree: (id: string) => void,
): NodeScriptAPI {
  const signals = link.signals;

  const syncFromData = (api: NodeScriptAPI) => {
    const t = link.data.transform;
    api.position.x = t.position.x;
    api.position.y = t.position.y;
    api.position.z = t.position.z;
    api.rotation.x = t.rotation.x;
    api.rotation.y = t.rotation.y;
    api.rotation.z = t.rotation.z;
    api.scale.x = t.scale.x;
    api.scale.y = t.scale.y;
    api.scale.z = t.scale.z;
    api.visible = link.data.visible;
    api.name = link.data.name;
    api.path = buildPath(link);
  };

  const applyTransform = () => {
    const o = link.object3d;
    if (!o) return;
    const t = link.data.transform;
    o.position.set(t.position.x, t.position.y, t.position.z);
    o.rotation.set(
      (t.rotation.x * Math.PI) / 180,
      (t.rotation.y * Math.PI) / 180,
      (t.rotation.z * Math.PI) / 180,
    );
    o.scale.set(t.scale.x, t.scale.y, t.scale.z);
    o.visible = link.data.visible;
  };

  const resolveApi = (n: TreeNodeLink): NodeScriptAPI =>
    n.script?.api ?? createNodeAPI(n, all, host, tree, onQueueFree);

  const api: NodeScriptAPI = {
    id: link.data.id,
    name: link.data.name,
    type: link.data.type,
    path: buildPath(link),
    visible: link.data.visible,
    position: { ...link.data.transform.position },
    rotation: { ...link.data.transform.rotation },
    scale: { ...link.data.transform.scale },
    props: link.data.props as Record<string, unknown>,

    get_parent() {
      return link.parent ? resolveApi(link.parent) : null;
    },
    get_children() {
      return link.children.map(resolveApi);
    },
    get_node(path: string) {
      return this.get_node_or_null(path);
    },
    get_node_or_null(path: string) {
      const hit = resolvePath(link, path);
      return hit ? resolveApi(hit) : null;
    },
    find_child(name: string, recursive = true) {
      const stack = [...link.children];
      while (stack.length) {
        const n = stack.shift()!;
        if (n.data.name === name) return resolveApi(n);
        if (recursive) stack.push(...n.children);
      }
      return null;
    },
    is_inside_tree() {
      return all.has(link.data.id);
    },
    get_tree() {
      return tree.createTreeAPI();
    },
    get_path() {
      return buildPath(link);
    },

    set_position(x, y, z) {
      link.data.transform.position = { x, y, z };
      syncFromData(api);
      applyTransform();
    },
    set_rotation_degrees(x, y, z) {
      link.data.transform.rotation = { x, y, z };
      syncFromData(api);
      applyTransform();
    },
    rotate_y(rad) {
      link.data.transform.rotation.y += (rad * 180) / Math.PI;
      syncFromData(api);
      applyTransform();
    },
    rotate_x(rad) {
      link.data.transform.rotation.x += (rad * 180) / Math.PI;
      syncFromData(api);
      applyTransform();
    },
    rotate_z(rad) {
      link.data.transform.rotation.z += (rad * 180) / Math.PI;
      syncFromData(api);
      applyTransform();
    },
    translate(x, y, z) {
      link.data.transform.position.x += x;
      link.data.transform.position.y += y;
      link.data.transform.position.z += z;
      syncFromData(api);
      applyTransform();
    },
    look_at(x, y, z) {
      if (link.object3d) {
        link.object3d.lookAt(x, y, z);
        const e = link.object3d.rotation;
        link.data.transform.rotation = {
          x: (e.x * 180) / Math.PI,
          y: (e.y * 180) / Math.PI,
          z: (e.z * 180) / Math.PI,
        };
        syncFromData(api);
      }
    },
    set_visible(v) {
      link.data.visible = v;
      if (link.object3d) link.object3d.visible = v;
      api.visible = v;
    },
    queue_free() {
      onQueueFree(link.data.id);
    },

    add_to_group(group) {
      tree.add_to_group(link.data.id, group);
      const g = (link.data.props.groups as string[]) || [];
      if (!g.includes(group)) {
        g.push(group);
        link.data.props.groups = g;
      }
    },
    remove_from_group(group) {
      tree.remove_from_group(link.data.id, group);
      const g = (link.data.props.groups as string[]) || [];
      link.data.props.groups = g.filter((x) => x !== group);
    },
    is_in_group(group) {
      return tree.get_groups_of(link.data.id).includes(group);
    },
    get_groups() {
      return tree.get_groups_of(link.data.id);
    },

    connect(signal, callable) {
      signals.signal(signal).connect(callable, { ownerId: link.data.id });
    },
    disconnect(signal, callable) {
      if (signals.has(signal)) signals.signal(signal).disconnect(callable);
    },
    is_connected(signal, callable) {
      return signals.has(signal) && signals.signal(signal).is_connected(callable);
    },
    emit_signal(signal, ...args) {
      if (signals.has(signal)) signals.signal(signal).emit(...args);
    },
    has_signal(signal) {
      return signals.has(signal);
    },

    call_deferred(method, ...args) {
      tree.call_deferred(() => {
        const fn = (api as Record<string, unknown>)[method];
        if (typeof fn === 'function') {
          (fn as (...a: unknown[]) => void).apply(api, args);
        } else if (link.script?.handlers[method] && typeof link.script.handlers[method] === 'function') {
          (link.script.handlers[method] as (...a: unknown[]) => void).apply(api, args);
        }
      });
    },

    move_and_slide(vx, vy, vz, delta) {
      // Simple kinematic move (piste bounds for CharacterBody3D)
      let x = link.data.transform.position.x + vx * delta;
      let y = link.data.transform.position.y + vy * delta;
      let z = link.data.transform.position.z + vz * delta;
      if (link.data.type === 'CharacterBody3D') {
        x = Math.max(-7, Math.min(7, x));
        z = Math.max(-0.6, Math.min(0.6, z));
        y = Math.max(0, y);
      }
      link.data.transform.position = { x, y, z };
      syncFromData(api);
      applyTransform();
    },

    log(...args) {
      host.log(`[${link.data.name}] ${args.map(String).join(' ')}`);
    },
    print(...args) {
      this.log(...args);
    },
  };

  return api;
}

function resolvePath(from: TreeNodeLink, path: string): TreeNodeLink | null {
  if (!path) return from;
  if (path.startsWith('/')) {
    const parts = path.split('/').filter(Boolean);
    let root = from;
    while (root.parent) root = root.parent;
    let cur: TreeNodeLink | null = root;
    let i = 0;
    if (parts[0] === root.data.name) i = 1;
    for (; i < parts.length; i++) {
      if (!cur) return null;
      cur = cur.children.find((c) => c.data.name === parts[i]) ?? null;
    }
    return cur;
  }
  const parts = path.split('/').filter(Boolean);
  let cur: TreeNodeLink | null = from;
  for (const p of parts) {
    if (!cur) return null;
    if (p === '.') continue;
    if (p === '..') {
      cur = cur.parent;
      continue;
    }
    cur = cur.children.find((c) => c.data.name === p) ?? null;
  }
  return cur;
}

export function bindScriptInstance(
  link: TreeNodeLink,
  handlers: ScriptHandlers,
  scriptProps: Record<string, unknown>,
  host: ScriptHost,
  tree: SceneTree,
  onQueueFree: (id: string) => void,
  signalNames: string[] = [],
): LiveScriptInstance {
  if (!link.signals) link.signals = new SignalBus();
  for (const n of signalNames) link.signals.signal(n);

  const api = createNodeAPI(link, new Map(), host, tree, onQueueFree);
  for (const [k, v] of Object.entries(handlers)) {
    if (typeof v !== 'function' && !k.startsWith('_')) api[k] = v;
  }
  for (const [k, v] of Object.entries(scriptProps)) api[k] = v;

  // Register groups from props
  const groups = (link.data.props.groups as string[]) || [];
  for (const g of groups) tree.add_to_group(link.data.id, g);

  const inst: LiveScriptInstance = {
    nodeId: link.data.id,
    handlers,
    api,
    ready: false,
    entered: false,
    error: null,
    signals: link.signals,
  };
  link.script = inst;
  return inst;
}

export function callLifecycle(
  inst: LiveScriptInstance,
  method: '_enter_tree' | '_ready' | '_exit_tree',
): void {
  const fn = inst.handlers[method];
  if (typeof fn !== 'function') return;
  try {
    fn.call(inst.api);
  } catch (e) {
    inst.error = (e as Error).message;
  }
}

export function callProcess(
  inst: LiveScriptInstance,
  method: '_process' | '_physics_process',
  dt: number,
): void {
  const fn = inst.handlers[method];
  if (typeof fn !== 'function') return;
  try {
    fn.call(inst.api, dt);
  } catch (e) {
    inst.error = (e as Error).message;
  }
}

export const DEFAULT_SCRIPT_TEMPLATE = `// Godot-style node script (JavaScript)
// @extends Node3D
// @export speed 1.0
// @export amplitude 0.25
// @signal spun

/**
 * Lifecycle: _enter_tree → children _ready → _ready → _process each frame
 * Globals: Input, Engine, print
 */

function _ready() {
  this.print("ready:", this.name, "path=", this.path);
  this.add_to_group("spinning");
}

function _process(delta) {
  this.rotate_y(delta * (this.speed || 1));
  const t = performance.now() / 1000;
  const a = this.amplitude || 0;
  if (a) {
    this.set_position(this.position.x, Math.sin(t * 2) * a, this.position.z);
  }
}
`;

export const PLAYER_SCRIPT_TEMPLATE = `// @extends CharacterBody3D
// @export move_speed 3.5
// @export lunge_impulse 6.0
// @signal lunged

function _ready() {
  this.print("Player controller ready on", this.name);
  this.add_to_group("fencers");
  this.add_to_group("player");
  this._cooldown = 0;
}

function _physics_process(delta) {
  // Godot-style input actions (see Project InputMap)
  const axis = Input.get_axis("move_left", "move_right");
  const fast = Input.is_action_pressed("move_fast") ? 1.6 : 1.0;
  const speed = (this.move_speed || 3.5) * fast;
  this.move_and_slide(axis * speed, 0, 0, delta);

  // Face direction of travel
  if (Math.abs(axis) > 0.01) {
    this.set_rotation_degrees(0, axis > 0 ? 0 : 180, 0);
  }

  this._cooldown = Math.max(0, (this._cooldown || 0) - delta);
  if (this._cooldown <= 0 && Input.is_action_just_pressed("lunge")) {
    const face = this.rotation.y > 90 ? -1 : 1;
    this.move_and_slide(face * (this.lunge_impulse || 6), 0, 0, 0.12);
    this.emit_signal("lunged");
    this._cooldown = 0.45;
    this.print("Lunge!");
  }

  // Optional bridge to real bout systems when Game autoload is present
  const Game = Engine.get_singleton("Game");
  if (Game && typeof Game.set_move === "function") {
    Game.set_move("player", axis);
    if (Input.is_action_just_pressed("lunge")) Game.action("player", "lunge");
    if (Input.is_action_just_pressed("fleche")) Game.action("player", "fleche");
    if (Input.is_action_just_pressed("parry_high")) Game.action("player", "parry_high");
    if (Input.is_action_just_pressed("parry_low")) Game.action("player", "parry_low");
    if (Input.is_action_just_pressed("riposte")) Game.action("player", "riposte");
  }
}
`;

export const AI_SCRIPT_TEMPLATE = `// @extends CharacterBody3D
// @export aggression 0.55
// @export reaction 0.35
// @export move_speed 2.8

function _ready() {
  this.print("AI opponent online — aggression", this.aggression);
  this.add_to_group("fencers");
  this.add_to_group("ai");
  this._t = 0;
  this._dir = -1;
}

function _physics_process(delta) {
  this._t = (this._t || 0) + delta;
  // Patrol toward center then retreat — editor / scene-play preview
  const target = Math.sin(this._t * (0.6 + (this.aggression || 0.5))) * 1.5 + 1.5;
  const dx = target - this.position.x;
  const step = Math.sign(dx) * Math.min(Math.abs(dx), (this.move_speed || 2.8) * delta);
  this.move_and_slide(step / Math.max(delta, 0.001), 0, 0, delta);
  this.set_rotation_degrees(0, 180, 0);

  const Game = Engine.get_singleton("Game");
  if (Game && typeof Game.set_move === "function") {
    Game.set_move("opponent", Math.sign(dx) * Math.min(1, Math.abs(dx)));
  }
}
`;
